import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Carrot, Plus, Edit, ScanLine, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { adminAPI } from '../../utils/api';
import { toast } from 'sonner';
import AdminNav from '../../components/admin/AdminNav';

// Admin -> Core Foods management surface.
// Lets a founder list/search Foods Core, spot unclassified records, and open a
// Core Food to set its authoritative grocery_category + allow_meal_scaling.
// Classification is manual & per-record — there is intentionally no automatic
// classification here.
export default function AdminCoreFoods() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [unclassifiedOnly, setUnclassifiedOnly] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    adminAPI.checkAdmin()
      .then(res => {
        if (res.data.is_admin) setIsAdmin(true);
        else { toast.error('Not authorized'); navigate('/home'); }
      })
      .catch(() => { toast.error('Not authorized'); navigate('/home'); })
      .finally(() => setCheckingAdmin(false));
  }, [navigate]);

  const load = useCallback(() => {
    if (!isAdmin) return;
    setLoading(true);
    adminAPI.listCoreFoods({
      q: searchQuery || undefined,
      unclassified_only: unclassifiedOnly,
      limit: 100,
    })
      .then(res => {
        setItems(res.data.items || []);
        setTotal(res.data.total || 0);
      })
      .catch(() => toast.error('Failed to load core foods'))
      .finally(() => setLoading(false));
  }, [isAdmin, searchQuery, unclassifiedOnly]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const isClassified = (f) => f.grocery_category != null && f.allow_meal_scaling != null;

  if (checkingAdmin) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">Checking access…</div>;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-core-foods">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background border-b border-border/40 header-safe-top">
        <div className="px-4 pb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate('/settings')} variant="ghost" size="sm" className="rounded-full" data-testid="core-foods-back-btn">
                <ArrowLeft size={20} />
              </Button>
              <div className="flex items-center gap-2">
                <Carrot size={24} className="text-accent" />
                <h1 className="font-heading font-bold text-xl">Core Foods</h1>
              </div>
            </div>
            <Button
              onClick={() => navigate('/admin/core-foods/new')}
              className="h-10 rounded-xl bg-accent text-white"
              data-testid="create-core-food-btn"
            >
              <Plus size={18} className="mr-1" />
              New Core Food
            </Button>
          </div>

          <AdminNav />

          {/* Search + filter */}
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search core foods by name…"
                className="pl-9 h-10 bg-secondary/30 border-transparent"
                data-testid="core-foods-search"
              />
            </div>
            <button
              onClick={() => setUnclassifiedOnly(v => !v)}
              className={`h-10 px-3 rounded-lg text-sm font-medium border transition-colors ${
                unclassifiedOnly
                  ? 'bg-amber-500/20 text-amber-600 border-amber-500/30'
                  : 'bg-secondary/30 text-muted-foreground border-transparent hover:bg-secondary/50'
              }`}
              data-testid="core-foods-unclassified-filter"
            >
              Unclassified only
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        <p className="text-xs text-muted-foreground" data-testid="core-foods-count">
          {loading ? 'Loading…' : `${items.length} shown of ${total} match${total === 1 ? '' : 'es'}`}
        </p>

        {!loading && items.length === 0 && (
          <div className="text-center text-muted-foreground py-12">
            <ScanLine size={32} className="mx-auto mb-2 opacity-50" />
            <p>No core foods found.</p>
          </div>
        )}

        {items.map((f) => (
          <button
            key={f.food_id}
            onClick={() => navigate(`/admin/core-foods/${f.food_id}/edit`)}
            className="w-full text-left bg-card border border-border/40 rounded-xl p-3 flex items-center justify-between hover:border-accent/40 transition-colors"
            data-testid={`core-food-row-${f.food_id}`}
          >
            <div className="min-w-0">
              <p className="font-medium text-sm truncate">{f.display_name || f.food_name}</p>
              <p className="text-xs text-muted-foreground truncate">
                {f.source ? `${f.source} • ` : ''}{Math.round(f.macros_per_100g?.calories || 0)} cal/100g
              </p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {f.grocery_category != null ? (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-secondary/50">{f.grocery_category}</span>
                ) : (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600">No grocery</span>
                )}
                {f.allow_meal_scaling === true && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600">Scalable</span>
                )}
                {f.allow_meal_scaling === false && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-500">No scaling</span>
                )}
                {f.allow_meal_scaling == null && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600">No scaling flag</span>
                )}
                {f.preference_groups_reviewed !== true && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600" data-testid={`core-food-unreviewed-${f.food_id}`}>Unreviewed</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-2">
              {isClassified(f)
                ? <CheckCircle2 size={18} className="text-emerald-500" />
                : <AlertCircle size={18} className="text-amber-500" />}
              <Edit size={16} className="text-muted-foreground" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
